import { io } from 'socket.io-client';

// Socket.IO connection URL - adjust this to match your backend
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000';

class TVSocket {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  /**
   * Connect to the Socket.IO server on /tv namespace
   */
  connect() {
    if (this.socket?.connected) {
      console.log('Socket already connected');
      return;
    }

    // Connect to /tv namespace
    this.socket = io(`${SOCKET_URL}/tv`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.socket.on('connect', () => {
      console.log('✅ Connected to /tv namespace - Socket ID:', this.socket.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Disconnected from /tv namespace:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
    });
  }

  /**
   * Disconnect from the Socket.IO server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  /**
   * Listen for socket connection event
   * @param {Function} callback - Callback function when socket connects
   */
  onConnect(callback) {
    if (this.socket) {
      this.socket.on('connect', callback);
    }
  }

  /**
   * Listen for socket disconnection event
   * @param {Function} callback - Callback function when socket disconnects
   */
  onDisconnect(callback) {
    if (this.socket) {
      this.socket.on('disconnect', callback);
    }
  }

  /**
   * Listen for TV list updates from backend
   * @param {Function} callback - Callback function to handle TV list updates
   */
  onTVListUpdate(callback) {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }

    this.socket.on('tv:list:update', (data) => {
      // Pass the full payload { tvs, devices, timestamp }. The consumer needs
      // both: `tvs` is the assigned-TV view (often empty), while `devices`
      // carries the real-time status for ALL devices — which is what drives
      // offline/online notifications. (Previously we passed only `data.tvs`,
      // so the empty array silently suppressed those notifications.)
      callback(data);
    });

    this.listeners.set('tv:list:update', callback);
  }

  /**
   * Remove TV list update listener
   */
  offTVListUpdate() {
    if (this.socket) {
      this.socket.off('tv:list:update');
      this.listeners.delete('tv:list:update');
    }
  }

  /**
   * Send play command to a TV
   * @param {string} tvId - The TV ID
   * @param {Object} content - Content object with type and url
   */
  playContent(tvId, content) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      throw new Error('Socket not connected');
    }

    console.log(`▶️ Sending play command to ${tvId}:`, content);
    this.socket.emit('tv:play', { tvId, content });
  }

  /**
   * Send stop command to a TV
   * @param {string} tvId - The TV ID
   */
  stopContent(tvId) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      throw new Error('Socket not connected');
    }

    console.log(`⏹️ Sending stop command to ${tvId}`);
    this.socket.emit('tv:stop', { tvId });
  }

  /**
   * Send reload command to a TV
   * @param {string} tvId - The TV ID
   */
  reloadTV(tvId) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      throw new Error('Socket not connected');
    }

    console.log(`🔄 Sending reload command to ${tvId}`);
    this.socket.emit('tv:reload', { tvId });
  }

  /**
   * Listen for screen resolution changes
   * @param {function} callback - ({deviceId, tvId, previousResolution, newResolution, timestamp}) => void
   */
  onResolutionChanged(callback) {
    if (!this.socket) return;
    this.socket.on('tv:resolution:changed', callback);
    this.listeners.set('tv:resolution:changed', callback);
  }

  /**
   * Listen for resolution mismatch warnings (content doesn't match new screen)
   * @param {function} callback - ({deviceId, tvId, newResolution, mismatchedSlots, timestamp}) => void
   */
  onResolutionMismatch(callback) {
    if (!this.socket) return;
    this.socket.on('tv:resolution:mismatch', callback);
    this.listeners.set('tv:resolution:mismatch', callback);
  }

  /**
   * Listen for hardware-change events raised by the backend screen-profile
   * system when the EDID fingerprint for a device changes in a way the
   * profile doesn't already expect. Payload shape (see backend
   * tv.socket.js processEdidReading for the authoritative source):
   *
   *   {
   *     deviceId, tvId,
   *     previous: {width, height, manufacturer, monitorName, hash},
   *     current:  {width, height, manufacturer, monitorName, hash},
   *     profileId,
   *     profileMatchesCurrent: boolean,
   *     blastRadius: {totalSlots, fits, broken, brokenList},
   *     timestamp,
   *   }
   *
   * The dashboard uses this to flag the affected device in the Screens
   * tab + render a HardwareChangeCard inside TVDetails.
   */
  onHardwareChanged(callback) {
    if (!this.socket) return;
    this.socket.on('tv:hardware:changed', callback);
    this.listeners.set('tv:hardware:changed', callback);
  }

  // `tv:health:update` is still broadcast by the backend, but this app no
  // longer subscribes — playback diagnostics live in the Watchdog Console.

  offResolutionEvents() {
    if (this.socket) {
      this.socket.off('tv:resolution:changed');
      this.socket.off('tv:resolution:mismatch');
      this.socket.off('tv:hardware:changed');
      this.listeners.delete('tv:resolution:changed');
      this.listeners.delete('tv:resolution:mismatch');
      this.listeners.delete('tv:hardware:changed');
    }
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.socket?.connected || false;
  }
}

// Export a singleton instance
export default new TVSocket();
